import { useEffect, useRef, type CSSProperties } from 'react'
import { cn } from '#app/utils/misc.tsx'

/**
 * The landing hero's live backdrop. Render it as a child of the hero
 * `<section>`; it covers the whole section, so the whole hero is hover area.
 *
 * Three things happen in it, all decorative and hidden from assistive
 * technology:
 *
 * - A spotlight lags behind the cursor anywhere in the section and reveals a
 *   hidden outlined wordmark plus the brand-lit dots under it.
 * - Ripples run through the dots: a quiet heartbeat every few seconds, and a
 *   stronger one from any element in the section that carries `data-ripple`
 *   when the pointer enters it (the trust pills and the CTAs).
 * - The text stays untouched. Elements marked `data-spotlight-keepout` are
 *   measured every frame: a dot inside one of their boxes stays off, with a
 *   soft feather around the edge, and the wordmark is confined to the band
 *   between the `top` keepouts (the pills) and the `bottom` keepouts (the
 *   heading and the copy), fading out before it reaches either.
 *
 * The lit dots are drawn on a canvas aligned to the section's resting
 * `bg-dot-pattern` (24px grid), so a lit dot is always a resting dot turned
 * on. Reduced motion renders one static frame with the beam resting on the
 * wordmark; devices without hover get the same resting beam plus ripples.
 */

const GAP = 24
const BEAM = 300
const RIPPLE_SPEED = 260 // px per second
const RIPPLE_LIFE = 2.4 // seconds
const RING = 96 // ring thickness in px
const HEARTBEAT_MS = 5000
const KEEPOUT_PAD = 16 // px added around a keepout box
const KEEPOUT_FEATHER = 48 // px over which dots come back on
const DOT_ALPHA = 0.55 // a lit dot never goes fully opaque

// the beam is measured from the stage; the wordmark layer sits in the band,
// so its mask takes the beam's y relative to the band
const WORDMARK_MASK =
	'radial-gradient(circle var(--sr) at var(--sx) var(--sy-band), black 0%, black 35%, transparent 80%)'
const BAND_MASK =
	'linear-gradient(to bottom, transparent, black 3rem, black calc(100% - 3rem), transparent)'

type Ripple = { x: number; y: number; born: number; strength: number }
type Box = { left: number; top: number; right: number; bottom: number }

function cssVar(name: string) {
	return getComputedStyle(document.documentElement)
		.getPropertyValue(name)
		.trim()
}

export function SpotlightStage({
	className,
	wordmark = 'OPEN SOURCED',
}: {
	className?: string
	wordmark?: string
}) {
	const stageRef = useRef<HTMLDivElement>(null)
	const canvasRef = useRef<HTMLCanvasElement>(null)

	useEffect(() => {
		const stage = stageRef.current
		const canvas = canvasRef.current
		if (!stage || !canvas || typeof ResizeObserver === 'undefined') return
		const ctx = canvas.getContext('2d')
		if (!ctx) return
		const section = stage.closest('section') ?? stage

		const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches
		const hoverless = window.matchMedia('(hover: none)').matches

		let w = 0
		let h = 0
		let keepouts: Box[] = []
		let bandTop = 0
		let bandBottom = 0
		const pointer = { x: 0, y: 0, inside: false }
		const spot = { x: 0, y: 0, r: 0 }
		const ripples: Ripple[] = []
		let brand = cssVar('--primary')
		let lastPalette = 0
		let lastBeat = 0
		let frame = 0
		let visible = true
		let running = false

		function measure() {
			const rect = stage!.getBoundingClientRect()
			keepouts = []
			let top = 0
			let bottom = rect.height
			for (const el of section.querySelectorAll<HTMLElement>(
				'[data-spotlight-keepout]',
			)) {
				const b = el.getBoundingClientRect()
				const box = {
					left: b.left - rect.left - KEEPOUT_PAD,
					top: b.top - rect.top - KEEPOUT_PAD,
					right: b.right - rect.left + KEEPOUT_PAD,
					bottom: b.bottom - rect.top + KEEPOUT_PAD,
				}
				keepouts.push(box)
				if (el.dataset.spotlightKeepout === 'top')
					top = Math.max(top, box.bottom)
				if (el.dataset.spotlightKeepout === 'bottom')
					bottom = Math.min(bottom, box.top)
			}
			bandTop = top
			bandBottom = Math.max(bottom, top)
			stage!.style.setProperty('--band-top', `${bandTop}px`)
			stage!.style.setProperty('--band-bottom', `${bandBottom}px`)
		}

		function resize() {
			const dpr = window.devicePixelRatio || 1
			const rect = stage!.getBoundingClientRect()
			w = rect.width
			h = rect.height
			canvas!.width = Math.round(w * dpr)
			canvas!.height = Math.round(h * dpr)
			ctx!.setTransform(dpr, 0, 0, dpr, 0, 0)
			measure()
		}

		function onMove(e: MouseEvent) {
			const rect = stage!.getBoundingClientRect()
			pointer.x = e.clientX - rect.left
			pointer.y = e.clientY - rect.top
			pointer.inside =
				pointer.x >= 0 && pointer.x <= w && pointer.y >= 0 && pointer.y <= h
		}
		function onLeave() {
			pointer.inside = false
		}
		function onOver(e: PointerEvent) {
			const target = e.target as Element | null
			const source = target?.closest<HTMLElement>('[data-ripple]')
			if (!source || !section.contains(source)) return
			// pointerover fires again when moving between the element's children
			if (e.relatedTarget && source.contains(e.relatedTarget as Node)) return
			const rect = stage!.getBoundingClientRect()
			const box = source.getBoundingClientRect()
			ripples.push({
				x: box.left + box.width / 2 - rect.left,
				y: box.top + box.height / 2 - rect.top,
				born: performance.now(),
				strength: 0.7,
			})
			start()
		}

		// 0 inside a keepout box, rising to 1 over the feather around it
		function keepoutFactor(x: number, y: number) {
			let factor = 1
			for (const k of keepouts) {
				const dx = Math.max(k.left - x, 0, x - k.right)
				const dy = Math.max(k.top - y, 0, y - k.bottom)
				factor = Math.min(factor, Math.hypot(dx, dy) / KEEPOUT_FEATHER)
				if (factor === 0) break
			}
			return factor
		}

		function draw(now: number) {
			if (now - lastPalette > 1000) {
				brand = cssVar('--primary')
				lastPalette = now
			}
			measure()
			const bandMid = (bandTop + bandBottom) / 2

			// beam: follow the cursor with lag; rest on the wordmark where there
			// is no cursor to follow
			if (still || hoverless) {
				spot.x = w / 2
				spot.y = bandMid
				spot.r = BEAM
			} else {
				if (pointer.inside) {
					spot.x += (pointer.x - spot.x) * 0.1
					spot.y += (pointer.y - spot.y) * 0.1
				}
				spot.r += ((pointer.inside ? BEAM : 0) - spot.r) * 0.08
			}
			stage!.style.setProperty('--sx', `${spot.x}px`)
			stage!.style.setProperty('--sy', `${spot.y}px`)
			stage!.style.setProperty('--sr', `${spot.r}px`)

			if (!still && now - lastBeat > HEARTBEAT_MS) {
				ripples.push({ x: w / 2, y: bandMid, born: now, strength: 0.3 })
				lastBeat = now
			}
			for (let k = ripples.length - 1; k >= 0; k--) {
				if (now - ripples[k]!.born > RIPPLE_LIFE * 1000) ripples.splice(k, 1)
			}

			ctx!.clearRect(0, 0, w, h)
			ctx!.fillStyle = brand
			for (let x = GAP / 2; x <= w; x += GAP) {
				for (let y = GAP / 2; y <= h; y += GAP) {
					let heat = 0
					if (spot.r > 1) {
						// smoothstep over the whole beam: no visible edge, no hot core
						const t = Math.min(
							Math.max(1 - Math.hypot(x - spot.x, y - spot.y) / spot.r, 0),
							1,
						)
						heat = t * t * (3 - 2 * t)
					}
					for (const rp of ripples) {
						const age = (now - rp.born) / 1000
						const band =
							1 -
							Math.abs(Math.hypot(x - rp.x, y - rp.y) - age * RIPPLE_SPEED) /
								RING
						if (band > 0) {
							heat = Math.max(
								heat,
								band * (1 - age / RIPPLE_LIFE) * rp.strength,
							)
						}
					}
					if (heat < 0.03) continue
					heat *= keepoutFactor(x, y)
					if (heat < 0.03) continue
					ctx!.globalAlpha = heat * DOT_ALPHA
					ctx!.beginPath()
					// same size as the resting dots; the beam only tints them
					ctx!.arc(x, y, 1 + heat * 0.15, 0, Math.PI * 2)
					ctx!.fill()
				}
			}
			ctx!.globalAlpha = 1

			if (still || !visible) {
				running = false
				return
			}
			frame = requestAnimationFrame(draw)
		}

		function start() {
			if (running || !visible) return
			running = true
			frame = requestAnimationFrame(draw)
		}

		resize()
		const resizeObserver = new ResizeObserver(resize)
		resizeObserver.observe(stage)
		const intersection =
			typeof IntersectionObserver === 'undefined'
				? null
				: new IntersectionObserver(([entry]) => {
						visible = entry?.isIntersecting ?? true
						if (visible) start()
					})
		intersection?.observe(stage)

		window.addEventListener('mousemove', onMove)
		document.addEventListener('mouseleave', onLeave)
		section.addEventListener('pointerover', onOver)
		start()

		return () => {
			cancelAnimationFrame(frame)
			resizeObserver.disconnect()
			intersection?.disconnect()
			window.removeEventListener('mousemove', onMove)
			document.removeEventListener('mouseleave', onLeave)
			section.removeEventListener('pointerover', onOver)
		}
	}, [])

	return (
		<div
			ref={stageRef}
			className={cn('absolute inset-0 overflow-hidden', className)}
			aria-hidden="true"
			style={
				{
					'--sx': '50%',
					'--sy': '50%',
					'--sr': '0px',
					'--band-top': '0px',
					'--band-bottom': '100%',
				} as CSSProperties
			}
		>
			<canvas ref={canvasRef} className="absolute inset-0 block size-full" />
			{/* the band between the pills and the heading: glow and wordmark live
			    here and fade out before they reach the text */}
			<div
				className="absolute inset-x-0"
				style={{
					top: 'var(--band-top)',
					height: 'calc(var(--band-bottom) - var(--band-top))',
					maskImage: BAND_MASK,
					WebkitMaskImage: BAND_MASK,
				}}
			>
				<div
					className="bg-primary pointer-events-none absolute size-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-[0.07] blur-[90px]"
					style={{
						left: 'var(--sx)',
						top: 'calc(var(--sy) - var(--band-top))',
					}}
				/>
				<div
					className="absolute inset-0 flex items-center"
					style={
						{
							maskImage: WORDMARK_MASK,
							WebkitMaskImage: WORDMARK_MASK,
							'--sy-band': 'calc(var(--sy) - var(--band-top))',
						} as CSSProperties
					}
				>
					{/* decorative outline, not copy: sized to the hero, not the type scale */}
					<p
						className="font-sans leading-none font-extrabold tracking-tighter whitespace-nowrap text-transparent select-none"
						style={{
							fontSize: 'clamp(6rem, 22vw, 20rem)',
							WebkitTextStroke: '2px var(--color-primary)',
							marginLeft: '-0.08em',
						}}
					>
						{wordmark}
					</p>
				</div>
			</div>
		</div>
	)
}
